/**
 * BulkImportDialog — 3-step wizard for importing leads via CSV/TSV paste.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, Badge } from '@/components/common';
import { useBulkParse, useBulkCommit, type BulkParseResult } from '../api/leads';
import { BULK_IMPORT_PREVIEW_ROWS } from '../config/ui';

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Instructions = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
  line-height: 1.6;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: monospace;
  font-size: 13px;
  resize: vertical;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $active, $done, theme }) =>
    $done ? theme.colors.success ?? '#22c55e' : $active ? theme.colors.primary : theme.colors.backgroundTertiary};
  color: ${({ $active, $done, theme }) =>
    $done || $active ? '#fff' : theme.colors.textSecondary};
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SummaryChip = styled.span<{ $color?: string }>`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $color }) => $color ? `${$color}20` : '#f1f5f9'};
  color: ${({ $color }) => $color ?? '#64748b'};
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
`;

const Td = styled.td<{ $error?: boolean }>`
  padding: 6px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
  color: ${({ $error, theme }) => $error ? theme.colors.error : 'inherit'};
`;

const StatusIcon = styled.span<{ $status: 'valid' | 'warning' | 'error' }>`
  color: ${({ $status }) =>
    $status === 'valid' ? '#22c55e' : $status === 'warning' ? '#f59e0b' : '#ef4444'};
  font-size: 15px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ResultRow = styled.tr<{ $status: 'created' | 'failed' }>`
  background: ${({ $status }) => $status === 'created' ? '#f0fdf4' : '#fef2f2'};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 1 | 2 | 3;

export const BulkImportDialog: React.FC<Props> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<Step>(1);
  const [rawText, setRawText] = useState('');
  const [parseResult, setParseResult] = useState<BulkParseResult | null>(null);

  const parseMutation = useBulkParse();
  const commitMutation = useBulkCommit();

  const handleClose = () => {
    setStep(1);
    setRawText('');
    setParseResult(null);
    parseMutation.reset();
    commitMutation.reset();
    onClose();
  };

  const handleParse = async () => {
    if (!rawText.trim()) return;
    const result = await parseMutation.mutateAsync({ raw_text: rawText });
    setParseResult(result);
    setStep(2);
  };

  const handleCommit = async () => {
    if (!parseResult) return;
    const validRows = parseResult.rows.filter((r) => r.is_valid);
    await commitMutation.mutateAsync({ rows: validRows });
    setStep(3);
  };

  const previewRows = parseResult?.rows.slice(0, BULK_IMPORT_PREVIEW_ROWS) ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Leads"
      size="xl"
      data-testid="bulk-import-dialog"
    >
      <StepIndicator>
        {([1, 2, 3] as Step[]).map((s) => (
          <StepDot key={s} $active={step === s} $done={step > s}>
            {step > s ? '✓' : s}
          </StepDot>
        ))}
        <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>
          {step === 1 ? 'Paste Data' : step === 2 ? 'Preview' : 'Results'}
        </span>
      </StepIndicator>

      {/* Step 1: Paste */}
      {step === 1 && (
        <>
          <Instructions>
            Paste CSV or TSV data below. Required column: <strong>phone</strong>.<br />
            Optional columns: <code>name</code> or <code>first_name</code> + <code>last_name</code>,{' '}
            <code>email</code>, <code>source</code>, <code>interests</code>, <code>notes</code>, <code>follow_up_date</code>.<br />
            The first row should be a header row.
          </Instructions>
          <TextArea
            data-testid="bulk-import-textarea"
            placeholder={"name,phone,email,source\nJohn Doe,+91 98765 43210,john@example.com,walk_in"}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
          />
          {parseMutation.isError && (
            <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>
              Parse failed. Please check your data format.
            </p>
          )}
          <ModalActions>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              data-testid="bulk-parse-btn"
              disabled={!rawText.trim() || parseMutation.isPending}
              onClick={handleParse}
            >
              {parseMutation.isPending ? 'Parsing…' : 'Parse'}
            </Button>
          </ModalActions>
        </>
      )}

      {/* Step 2: Preview */}
      {step === 2 && parseResult && (
        <>
          <SummaryRow>
            <SummaryChip $color="#22c55e">{parseResult.valid_rows} valid</SummaryChip>
            <SummaryChip $color="#f59e0b">{parseResult.duplicate_warning_rows} warnings</SummaryChip>
            <SummaryChip $color="#ef4444">{parseResult.error_rows} errors</SummaryChip>
            <SummaryChip>{parseResult.total_rows} total</SummaryChip>
          </SummaryRow>
          <div style={{ overflowX: 'auto' }}>
            <PreviewTable data-testid="bulk-preview-table">
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Email</Th>
                  <Th>Source</Th>
                  <Th>Interests</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const status = row.errors.length > 0 ? 'error' : row.is_duplicate_warning ? 'warning' : 'valid';
                  return (
                    <tr key={row.row_index} data-testid={`bulk-row-${row.row_index}`}>
                      <Td>{row.row_index + 1}</Td>
                      <Td>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</Td>
                      <Td>{row.phone || '—'}</Td>
                      <Td>{row.email || '—'}</Td>
                      <Td>{row.source || '—'}</Td>
                      <Td>{row.interests?.join(', ') || '—'}</Td>
                      <Td $error={status === 'error'}>
                        <StatusIcon $status={status}>
                          {status === 'valid' ? '✓' : status === 'warning' ? '⚠' : '✗'}
                        </StatusIcon>
                        {row.errors.length > 0 && (
                          <span style={{ fontSize: 12, marginLeft: 4 }}>{row.errors.join('; ')}</span>
                        )}
                        {status === 'warning' && (
                          <span style={{ fontSize: 12, marginLeft: 4 }}>Duplicate warning</span>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </PreviewTable>
          </div>
          {parseResult.total_rows > BULK_IMPORT_PREVIEW_ROWS && (
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              Showing first {BULK_IMPORT_PREVIEW_ROWS} of {parseResult.total_rows} rows.
            </p>
          )}
          <ModalActions>
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button
              data-testid="bulk-commit-btn"
              disabled={parseResult.valid_rows === 0 || commitMutation.isPending}
              onClick={handleCommit}
            >
              {commitMutation.isPending ? 'Importing…' : `Import ${parseResult.valid_rows} Lead${parseResult.valid_rows !== 1 ? 's' : ''}`}
            </Button>
          </ModalActions>
        </>
      )}

      {/* Step 3: Results */}
      {step === 3 && commitMutation.data && (
        <>
          <SummaryRow>
            <SummaryChip $color="#22c55e">{commitMutation.data.created} created</SummaryChip>
            {commitMutation.data.failed > 0 && (
              <SummaryChip $color="#ef4444">{commitMutation.data.failed} failed</SummaryChip>
            )}
          </SummaryRow>
          <div style={{ overflowX: 'auto' }}>
            <PreviewTable data-testid="bulk-results-table">
              <thead>
                <tr>
                  <Th>Row</Th>
                  <Th>Status</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {commitMutation.data.results.map((r) => (
                  <ResultRow key={r.row_index} $status={r.status}>
                    <Td>{r.row_index + 1}</Td>
                    <Td>
                      <Badge
                        variant={r.status === 'created' ? 'success' : 'error'}
                        size="sm"
                      >
                        {r.status}
                      </Badge>
                    </Td>
                    <Td $error={r.status === 'failed'}>
                      {r.status === 'created' ? r.lead_id : r.error ?? '—'}
                    </Td>
                  </ResultRow>
                ))}
              </tbody>
            </PreviewTable>
          </div>
          <ModalActions>
            <Button onClick={handleClose}>Done</Button>
          </ModalActions>
        </>
      )}
    </Modal>
  );
};
